/** Machine-readable code on step-mismatch 422 responses. */
export const ANALYSIS_WRONG_STEP_CODE = 'ANALYSIS_WRONG_STEP' as const;

/** Laravel-style error body returned by the API. */
export interface ApiErrorBody {
  readonly message?: string;
  readonly errors?: Readonly<Record<string, readonly string[]>>;
  /** Provider identifier on 500/503 (ex. ProviderUnavailableException). */
  readonly error?: string;
  /** Machine-readable business code (ex. `ANALYSIS_WRONG_STEP`). */
  readonly code?: string;
  /** Server-side intake step when the mutation targets the wrong screen. */
  readonly current_step?: string | null;
}

/** Normalized API error preserved for feature consumers. */
export interface ApiError {
  readonly status: number;
  readonly message: string | null;
  readonly errors: Readonly<Record<string, readonly string[]>>;
  readonly providerError: string | null;
  readonly retryAfter: number | null;
}

/** Virtual business-rule keys that are not form fields. */
export const API_VIRTUAL_RULE_KEYS = ['analysis', 'step', 'document'] as const;

export type ApiVirtualRuleKey = (typeof API_VIRTUAL_RULE_KEYS)[number];

/**
 * Structured view of a `422`: known field keys, virtual rules (`analysis`, `step`,
 * `document`), and everything else surfaced as visible fallback.
 */
export interface ValidationErrorClassification {
  readonly message: string | null;
  readonly fieldErrors: Readonly<Record<string, readonly string[]>>;
  readonly virtualRuleErrors: Readonly<Record<string, readonly string[]>>;
  readonly unknownErrors: Readonly<Record<string, readonly string[]>>;
  /** Flat list of every message, for unknown-key and top-alert fallback. */
  readonly allMessages: readonly string[];
  /** Machine-readable business code from the error body. */
  readonly code: string | null;
  /** Server-side intake step when the mutation targets the wrong screen. */
  readonly currentStep: string | null;
}

/** One scalar field on a collection card, vínculo or crop line after snapshot projection. */
export interface OperationCollectionStableFieldError {
  readonly wirePath: string;
  readonly field: string;
  readonly messages: readonly string[];
}

/** Field errors for one operation card, keyed by stable local keys from the persist snapshot. */
export interface OperationCollectionProjectedCardErrors {
  readonly cardLocalKey: string;
  readonly operationIndex: number;
  readonly fields: readonly OperationCollectionStableFieldError[];
  /** Per `operation_landholding_id`; nested crop lines carry `crops.K.field`. */
  readonly landholdings: Readonly<Record<string, readonly OperationCollectionStableFieldError[]>>;
  readonly operationCrops: Readonly<Record<string, readonly OperationCollectionStableFieldError[]>>;
}

/**
 * `422` field paths projected onto the frozen POST snapshot. Persist indices never
 * follow a live reorder or removal on screen.
 */
export interface OperationCollectionValidationProjection {
  readonly snapshotCapturedAtMs: number;
  readonly rootFieldErrors: Readonly<Record<string, readonly string[]>>;
  readonly cards: readonly OperationCollectionProjectedCardErrors[];
  readonly virtualRuleErrors: Readonly<Record<string, readonly string[]>>;
  readonly unknownErrors: Readonly<Record<string, readonly string[]>>;
  readonly unmappedWirePaths: Readonly<Record<string, readonly string[]>>;
}
